import { Link, MenuItem, Stack, Typography } from '@mui/material';
import { ContractPageVersion } from '@vy/api-definition';
import { formatDate } from '../../../../../utils/date';
import React from 'react';
import { last, NonEmptyArray, Optional } from '@vy/utils';
import { isCtrlClick } from '../../../../../utils/click-event-utils';
import { VyIcon } from '@vy/ui-design-system';
import { VyDivider } from '../../../../../components/new-design/atoms';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { useDialog } from '../../../../../hooks/use-dialog';

type ContractVersionMenuItemsProps = {
  versions: NonEmptyArray<ContractPageVersion>;
  versionIdFromUrl: Optional<string>;
  setVersionIdInUrl: (revisionId: Optional<string>) => void;
  handleMenuClose: () => void;
  getVersionLabel: (index: number) => string;
  onDeleteRevision?: (revisionId: Optional<string>) => void;
};

export const ContractVersionMenuItems: React.FC<ContractVersionMenuItemsProps> = ({
  versions,
  versionIdFromUrl,
  setVersionIdInUrl,
  handleMenuClose,
  getVersionLabel,
  onDeleteRevision,
}) => {
  const { handleClose, handleOpen, isDialogOpen } = useDialog();
  const lastVersion = last(versions);

  const handleDeleteClick = () => {
    onDeleteRevision?.(lastVersion?.revisionId);
    handleClose();
  };

  return (
    <>
      {versions.map((version, index) => {
        const versionLabel = getVersionLabel(index);
        const isSelected = version.revisionId === versionIdFromUrl;

        return (
          <MenuItem
            sx={{
              minWidth: '176px',
              lineHeight: '18px',
              fontSize: '12px',
            }}
            key={version.revisionId ?? index}
            selected={isSelected}
            onClick={() => {
              setVersionIdInUrl(version.revisionId);
              handleMenuClose();
            }}
          >
            <Link
              href={getVersionUrl(version.revisionId)}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
              }}
              onClick={(e) => {
                if (isCtrlClick(e)) {
                  return e.stopPropagation();
                }

                return e.preventDefault();
              }}
            >
              {`${versionLabel}: Start date ${formatDate(version.effectiveAt)}`}
            </Link>
          </MenuItem>
        );
      })}
      <VyDivider />
      <MenuItem
        sx={{
          minWidth: '176px',
          fontSize: '12px',
        }}
        disabled={!!lastVersion.isNonDeletable}
        onClick={handleOpen}
      >
        <Stack
          direction={'row'}
          sx={{
            alignItems: 'center',
            justifyContent: 'start'
          }}>
          <VyIcon
            style={{
              transform: 'scale(0.8)',
              marginLeft: '-8px',
            }}
            icon="Delete"
          />
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 400
            }}>
            Delete the latest phase
          </Typography>
        </Stack>
      </MenuItem>
      <VyDialog
        maxWidth="sm"
        label="Delete the latest phase?"
        ctaText={'Yes, delete phase'}
        cancelButtonText={'Cancel'}
        onClose={handleClose}
        onSubmit={handleDeleteClick}
        ctaDisabled={!!lastVersion.isNonDeletable}
        open={isDialogOpen}
        ctaButtonProps={{
          disabled: !!lastVersion.isNonDeletable,
          sx: {
            backgroundColor: '#FF2E57',
            '&:hover': {
              backgroundColor: '#c92041',
            },
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 400
          }}>
          {`This will delete the latest contract phase, and revert back to the previous phase. This action is
          final and cannot be undone.`}
        </Typography>
      </VyDialog>
    </>
  );
};

function getVersionUrl(revisionId: Optional<string>) {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set('versionId', revisionId ?? '');

  return currentUrl.toString();
}
